import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import StepPipeline from "@/components/StepPipeline";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_COLORS, STEPS } from "@/lib/steps";
import {
  Building2, Calendar, ChevronLeft, ChevronRight, PlusCircle, Users, TrendingUp,
  CheckCircle2, Clock, AlertOctagon, AlertTriangle, Search, GraduationCap,
} from "lucide-react";
import { formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { cn, hasRole } from "@/lib/utils";

const ROLE_ACCENT = {
  admin: "border-l-slate-400", bd: "border-l-blue-500", pm: "border-l-indigo-500",
  it: "border-l-cyan-500", finance: "border-l-emerald-500", hr: "border-l-amber-500",
  client: "border-l-fuchsia-500", faculty: "border-l-teal-500",
};

function isOverdue(p) {
  const due = p.current_step_info?.due_date;
  return p.status === "active" && !!due && new Date(due) < new Date();
}

function dueLabel(dueIso) {
  if (!dueIso) return null;
  const days = Math.ceil((new Date(dueIso) - new Date()) / 86400000);
  if (days < 0) return { text: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`, tone: "text-rose-600" };
  if (days === 0) return { text: "Due today", tone: "text-amber-600" };
  if (days === 1) return { text: "Due tomorrow", tone: "text-amber-600" };
  return { text: `Due in ${days} days`, tone: "text-muted-foreground" };
}

// Faculty may be recorded as a multi-faculty array (faculty_names) or, on older
// programs, only the legacy singular faculty_name — normalize to an array either way.
function facultyNames(program) {
  if (program.faculty_names?.length) return program.faculty_names;
  return program.faculty_name ? [program.faculty_name] : [];
}

// The list endpoint only returns the current step's real status — every step before it
// is, by construction, already completed (the workflow only ever advances sequentially).
function pipelineSteps(program) {
  const cs = program.current_step_info;
  return STEPS.map((s) => {
    if (s.number < program.current_step) return { step_number: s.number, status: "completed" };
    if (s.number === program.current_step && cs) return cs;
    return { step_number: s.number, status: "pending" };
  });
}

const PAGE_SIZE = 10;

// Windowed page-number list with "…" gaps, e.g. [1, "…", 4, 5, 6, "…", 12].
function pageList(current, total) {
  const delta = 2;
  const range = [];
  for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) range.push(i);
  if (range[0] > 1) {
    if (range[0] > 2) range.unshift("…");
    range.unshift(1);
  }
  if (range[range.length - 1] < total) {
    if (range[range.length - 1] < total - 1) range.push("…");
    range.push(total);
  }
  return range;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  // List state lives in the URL (?page=9&filter=overdue&q=...&month=2026-08) rather than component
  // state, so opening a program and coming Back restores the same page, filter and search.
  // Defaults are omitted from the URL; replace:true keeps paging/typing out of browser history.
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all"; // all | active | overdue | closed | mine
  const search = params.get("q") || "";
  const monthFilter = params.get("month") || "all";
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const updateParams = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === null || v === "" || v === "all" || (k === "page" && Number(v) === 1)) next.delete(k);
      else next.set(k, String(v));
    });
    return next;
  }, { replace: true });
  // Changing a filter or search starts again from page 1.
  const setFilter = (v) => updateParams({ filter: v, page: null });
  const setSearch = (v) => updateParams({ q: v, page: null });
  const setMonthFilter = (v) => updateParams({ month: v, page: null });
  const setPage = (p) => updateParams({ page: p });
  const hasOwnedSteps = user.roles.some((r) => !["client", "faculty"].includes(r));
  const myLabel = user.roles.filter((r) => !["client", "faculty"].includes(r)).map((r) => ROLE_LABELS[r]).join(" / ");

  const load = async () => {
    try {
      const { data } = await api.get("/programs");
      setPrograms(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const matchesStatusFilter = (p) => {
    if (filter === "active") return p.status === "active";
    if (filter === "overdue") return isOverdue(p);
    if (filter === "closed") return p.status === "closed";
    if (filter === "mine") return user.roles.includes(p.current_step_info?.owner);
    return true;
  };

  const matchesSearch = (p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      p.training_title?.toLowerCase().includes(q) ||
      p.client_name?.toLowerCase().includes(q) ||
      facultyNames(p).some((n) => n.toLowerCase().includes(q))
    );
  };

  const matchesMonth = (p) => monthFilter === "all" || p.start_date?.slice(0, 7) === monthFilter;

  const monthOptions = useMemo(() => {
    const set = new Set();
    programs.forEach((p) => { if (p.start_date) set.add(p.start_date.slice(0, 7)); });
    return [...set].sort().reverse();
  }, [programs]);

  const monthLabel = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  };

  const filtered = programs.filter((p) => matchesStatusFilter(p) && matchesSearch(p) && matchesMonth(p));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const stats = {
    total: programs.length,
    active: programs.filter((p) => p.status === "active").length,
    closed: programs.filter((p) => p.status === "closed").length,
    mine: programs.filter((p) => user.roles.includes(p.current_step_info?.owner) && p.status === "active").length,
    overdue: programs.filter(isOverdue).length,
  };

  const TONES = {
    primary: { card: "from-indigo-50/70", icon: "bg-gradient-to-br from-indigo-500 to-blue-500 text-white shadow-md shadow-indigo-500/25", top: "bg-indigo-500" },
    blue: { card: "from-blue-50/70", icon: "bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-md shadow-blue-500/25", top: "bg-blue-500" },
    emerald: { card: "from-emerald-50/70", icon: "bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/25", top: "bg-emerald-500" },
    amber: { card: "from-amber-50/70", icon: "bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/25", top: "bg-amber-500" },
    rose: { card: "from-rose-50/70", icon: "bg-gradient-to-br from-rose-500 to-pink-500 text-white shadow-md shadow-rose-500/25", top: "bg-rose-500" },
  };

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto page-enter">
      <header className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-br from-indigo-50 via-blue-50/60 to-cyan-50/50 p-6 sm:p-8">
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-gradient-to-br from-blue-400/25 to-cyan-300/10 blur-3xl" />
          <div className="absolute -bottom-20 left-1/4 h-52 w-52 rounded-full bg-gradient-to-br from-indigo-400/20 to-violet-300/10 blur-3xl" />
        </div>
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-primary/70 font-semibold">Workflow Dashboard</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1">
              {user.roles.includes("admin") ? "Hello Admin." : `Hello, ${user.name.split(" ")[0]}.`}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              You are signed in as <span className="font-medium text-foreground">{user.roles.map((r) => ROLE_LABELS[r]).join(" + ")}</span>.{" "}
              {!hasOwnedSteps && user.roles.includes("client") && "Track your training programs in one place."}
              {!hasOwnedSteps && user.roles.includes("faculty") && "Track the courses assigned to you and their progress."}
              {hasOwnedSteps && "Track every training program in one place."}
            </p>
          </div>
          {hasRole(user, "bd") && (
            <Link to="/programs/new">
              <Button data-testid="new-program-btn" className="gap-2 shadow-md shadow-primary/20">
                <PlusCircle className="w-4 h-4" /> New Training Program
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Stat cards */}
      <div className={`grid grid-cols-2 ${hasOwnedSteps ? "lg:grid-cols-5" : "lg:grid-cols-3"} gap-4 mt-8`} data-testid="stats-grid">
        <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Total Programs" value={stats.total}
          tone="primary" tones={TONES} testid="stat-total" />
        <StatCard icon={<Clock className="w-4 h-4" />} label="Active" value={stats.active}
          tone="blue" tones={TONES} testid="stat-active" />
        <StatCard icon={<CheckCircle2 className="w-4 h-4" />} label="Closed" value={stats.closed}
          tone="emerald" tones={TONES} testid="stat-closed" />
        {hasOwnedSteps && (
          <StatCard icon={<AlertOctagon className="w-4 h-4" />} label="Awaiting My Team" value={stats.mine}
            tone="amber" tones={TONES} testid="stat-mine" />
        )}
        <StatCard icon={<AlertTriangle className="w-4 h-4" />} label="Overdue" value={stats.overdue}
          tone="rose" tones={TONES} testid="stat-overdue" />
      </div>

      {/* Search + month filter */}
      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            data-testid="program-search"
            placeholder="Search by name, faculty, or client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger data-testid="month-filter" className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {monthOptions.map((ym) => <SelectItem key={ym} value={ym}>{monthLabel(ym)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Filters */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {[
          ["all", "All", stats.total, ""],
          ["active", "Active", stats.active, "border-blue-300 text-blue-700 hover:bg-blue-50"],
          ["overdue", "Overdue", stats.overdue, "border-rose-300 text-rose-700 hover:bg-rose-50"],
          ["closed", "Closed", stats.closed, "border-emerald-300 text-emerald-700 hover:bg-emerald-50"],
          ...(hasOwnedSteps ? [["mine", `Awaiting ${myLabel}`, stats.mine, "border-amber-300 text-amber-700 hover:bg-amber-50"]] : []),
        ].map(([k, l, count, tone]) => (
          <Button
            key={k}
            variant={filter === k ? "default" : "outline"}
            size="sm"
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={cn(filter !== k && count > 0 && tone)}
          >{l} · {count}</Button>
        ))}
      </div>

      {/* Programs list with pipeline */}
      <div className="mt-6 space-y-4">
        {loading && <div className="text-sm text-muted-foreground">Loading programs...</div>}
        {!loading && filtered.length === 0 && programs.length > 0 && (search.trim() || monthFilter !== "all") && (
          <Card className="border-dashed">
            <CardContent className="p-10 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center mb-4 shadow-lg shadow-primary/20">
                <Search className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-semibold">No matching programs</h3>
              <p className="text-sm text-muted-foreground mt-1">Try a different name, faculty, client, or month.</p>
            </CardContent>
          </Card>
        )}
        {!loading && filtered.length === 0 && (programs.length === 0 || !(search.trim() || monthFilter !== "all")) && (
          <Card className="border-dashed">
            <CardContent className="p-10 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center mb-4 shadow-lg shadow-primary/20">
                <Building2 className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-semibold">No programs to show</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {hasRole(user, "bd") && "Kick off your first corporate training program."}
                {!hasRole(user, "bd") && !hasOwnedSteps && user.roles.includes("client") && "No training programs are linked to your account yet."}
                {!hasRole(user, "bd") && !hasOwnedSteps && user.roles.includes("faculty") && "You haven't been assigned to any courses yet."}
                {!hasRole(user, "bd") && hasOwnedSteps && "No programs yet. BD team will initiate."}
              </p>
              {hasRole(user, "bd") && (
                <Link to="/programs/new" className="inline-block mt-4">
                  <Button data-testid="empty-new-program"><PlusCircle className="w-4 h-4 mr-2" /> Create Program</Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        {paged.map((p, i) => (
          <ProgramRow key={p.id} program={p} delay={Math.min(i, 8) * 0.05} />
        ))}

        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
            <div className="text-xs text-muted-foreground">
              Showing {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, filtered.length)} of {filtered.length}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5" data-testid="programs-pagination">
                <Button variant="outline" size="sm" data-testid="page-prev" disabled={pageSafe === 1} onClick={() => setPage(pageSafe - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {pageList(pageSafe, totalPages).map((p, i) => (
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} className="px-1.5 text-sm text-muted-foreground">…</span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === pageSafe ? "default" : "outline"}
                      size="sm"
                      className="w-9"
                      data-testid={`page-${p}`}
                      onClick={() => setPage(p)}
                    >{p}</Button>
                  )
                ))}
                <Button variant="outline" size="sm" data-testid="page-next" disabled={pageSafe === totalPages} onClick={() => setPage(pageSafe + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, subtext, tone, tones, testid }) {
  const t = tones[tone];
  return (
    <Card data-testid={testid} className={`group relative border-slate-200/80 shadow-soft overflow-hidden card-hover bg-gradient-to-br ${t.card} to-white`}>
      <div className={`absolute inset-x-0 top-0 h-[3px] ${t.top}`} />
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground truncate">{label}</div>
            <div className="text-3xl font-heading font-semibold tracking-tight mt-1.5 text-slate-900 tabular-nums">{value}</div>
            {subtext && <div className="text-[11px] text-muted-foreground mt-1 truncate">{subtext}</div>}
          </div>
          <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${t.icon} transition-transform duration-300 ease-flowy group-hover:scale-110 group-hover:rotate-3`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgramRow({ program, delay = 0 }) {
  const cs = program.current_step_info;
  const stepMeta = STEPS.find((s) => s.number === program.current_step);
  const overdue = isOverdue(program);
  const blocked = cs?.status === "blocked";
  const due = dueLabel(cs?.due_date);
  const percent = program.status === "closed" ? 100 : Math.round(((program.current_step - 1) / STEPS.length) * 100);
  const progressTone = blocked ? "bg-red-500" : overdue ? "bg-rose-500" : program.status === "closed" ? "bg-emerald-500" : "bg-blue-500";

  return (
    <Card data-testid={`program-row-${program.id}`}
      style={{ animationDelay: `${delay}s` }}
      className={cn("border-border border-l-4 card-hover animate-fade-in-up", ROLE_ACCENT[cs?.owner] || "border-l-slate-300")}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/programs/${program.id}`} className="font-heading text-lg font-semibold tracking-tight hover:text-primary transition-colors">
                {program.training_title}
              </Link>
              <Badge className={cn("font-semibold", program.status === "closed"
                ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                : "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-100")}>
                {program.status.toUpperCase()}
              </Badge>
              {blocked && (
                <Badge className="bg-red-100 text-red-800 border-red-300 hover:bg-red-100 gap-1">
                  <AlertOctagon className="w-3 h-3" /> BLOCKED
                </Badge>
              )}
              {!blocked && overdue && (
                <Badge className="bg-rose-100 text-rose-800 border-rose-300 hover:bg-rose-100 gap-1">
                  <AlertTriangle className="w-3 h-3" /> OVERDUE
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1.5 flex-wrap">
              <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {program.client_name}</span>
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Batch of {program.batch_size || "-"}</span>
              {facultyNames(program).length > 0 && (
                <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {facultyNames(program).join(", ")}</span>
              )}
              {program.start_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {program.start_date} → {program.end_date || "?"}</span>}
              <span>Created by {program.created_by_name}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right min-w-[140px]">
              <div className="flex items-center justify-end gap-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Current Step</div>
                {cs?.owner && (
                  <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded-full border", ROLE_COLORS[cs.owner])}>
                    {ROLE_LABELS[cs.owner]}
                  </span>
                )}
              </div>
              <div className="text-sm font-semibold mt-0.5">{program.current_step}/{STEPS.length} · {stepMeta?.title}</div>
              {due && program.status !== "closed" && (
                <div className={`text-[11px] font-medium mt-0.5 ${due.tone}`}>{due.text}</div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <Progress value={percent} className="h-1.5 w-24 bg-slate-200" indicatorClassName={progressTone} />
                <span className="text-[10px] font-medium text-muted-foreground w-8 text-right">{percent}%</span>
              </div>
            </div>
            <Link to={`/programs/${program.id}`}>
              <Button variant="outline" size="sm" data-testid={`view-program-${program.id}`}>
                Open <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        <div className="mt-5 rounded-lg bg-slate-50/60 border border-slate-100 p-3">
          <StepPipeline steps={pipelineSteps(program)} />
        </div>
      </CardContent>
    </Card>
  );
}
